import {
  useMemo,
  useState,
} from 'react';
import MultiSelect from 'library/components/multiselect';
import RadioGroup from 'library/components/radio-group';
import Dropdown from 'library/components/dropdown';
import { FILTER_KEYS, SORT_DIRECTION as DIRECTION, TYPE as TYPES } from 'store/browse-items/fixtures';
import useStorageList from 'library/hooks/use-deferred-data/use-storage-list';
import useCategoryList from 'library/hooks/use-deferred-data/use-items-category-list';
import useManufacturerList from 'library/hooks/use-deferred-data/use-manufacturer-list';
import { useDispatch } from 'react-redux';
import {
  Container,
  Reset,
  Icon,
  Wrapper,
} from './styles';
import { DEFAULT_SORTING_OPTIONS, ITEM_SORTING_OPTIONS, SET_SORTING_OPTIONS } from './constants';
import { getSortingValue, getFilterValues } from './helpers';
import SearchQuery from '../search-query';
import { resetItemsCategories } from '../../../store/items-categories/actions';

const {
  CATEGORIES,
  SORT_BY,
  SORT_DIRECTION,
  MANUFACTURERS,
  TYPE,
  STORAGE,
  QUANTITY,
} = FILTER_KEYS;

const typeOptions = [
  { label: 'Items only', value: 'item' },
  { label: 'Sets only', value: 'set' },
];

const qtyOptions = [
  { label: 'Any', value: null },
  { label: 'None', value: 0 },
];

const getFilterQueryData = ({
  value,
  type,
  valueMap,
}) => value.reduce(({ data, namesMap }, id) => {
  if(namesMap[valueMap[id]]) {
    return {
      data,
      namesMap: {
        ...namesMap,
        [valueMap[id]]: [...namesMap[valueMap[id]], id],
      },
    };
  }

  return {
    data: data.concat([{ type, value: id, text: valueMap[id] }]),
    namesMap: {
      ...namesMap,
      [valueMap[id]]: [id],
    },
  };
}, { data: [], namesMap: {} });

const DEFERRED_FILTERS = [MANUFACTURERS, CATEGORIES, STORAGE];
const DEFAULT_SKIP_STATE = {
  [MANUFACTURERS]: false,
  [CATEGORIES]: false,
  [STORAGE]: false,
};

const Filters = ({
  hasEmptyManufacturers = false,
  hasEmptyStorages = false,
  showType = true,
  isSetOptionsContent = false,
  showStorage = true,
  onChange,
  onReset,
  onDelete,
  filters,
  isShown,
  isLongerModal = false,
  baseQuery,
  children,
  isQueryDisplayed = true,
  hasAdditionalSortOptions = false,
  isQueryFilterSibling = true,
  areInactiveCategoriesIncluded = false,
  multiselectProcessDelayMS = 0,
  isLoading = false,
  isContentDisabledWhenOpen = false,
}) => {
  const dispatch = useDispatch();
  const [isSkipped, setDataLoaded] = useState(DEFAULT_SKIP_STATE);
  const {
    [MANUFACTURERS]: manufacturersValue,
    [CATEGORIES]: categoryValue,
    [STORAGE]: storageValue,
  } = filters;
  const { options: categoriesOptions, labelMap: categoryNames, onQueryChange: onCategoryQueryChange } = useCategoryList(
    {
      skip: isSkipped[CATEGORIES],
      areInactiveCategoriesIncluded,
    },
  );
  const {
    options: manufacturersOptions, labelMap: manufacturerNames, onQueryChange: onManufacturerQueryChange, nameToIdsMap,
  } = useManufacturerList(
    isSkipped[MANUFACTURERS],
    undefined,
    false,
  );
  const manufacturerLocalNames = {
    ...manufacturerNames,
    ...(hasEmptyManufacturers ? { empty: '<Empty value>' } : {}),
  };
  const { options: storagesOptions, labelMap: storageNames, onQueryChange: onStorageQueryChange } = useStorageList(
    isSkipped[STORAGE] || storageValue === undefined,
  );

  const storageLocationLocalNames = {
    ...storageNames,
    ...(hasEmptyStorages ? { empty: '<Empty value>' } : {}),
  };

  const handleOpen = (field) => {
    if(isSkipped[field] === true || ([MANUFACTURERS].includes(field)) || ([STORAGE].includes(field))) {
      setDataLoaded((prevState) => ({
        ...prevState,
        [field]: false,
      }));
    }
  };

  const handleManufacturerSearch = (value) => {
    onManufacturerQueryChange(value);

    if(isSkipped[MANUFACTURERS] === true && value) {
      setDataLoaded((prevState) => ({
        ...prevState,
        [MANUFACTURERS]: false,
      }));
    }
  };

  const handleClose = (field) => {
    setDataLoaded((prevState) => ({
      ...prevState,
      [field]: true,
    }));
  };

  const handleCategoriesClose = () => {
    dispatch(resetItemsCategories());

    setDataLoaded((prevState) => ({
      ...prevState,
      [CATEGORIES]: true,
    }));
  };

  const handleCategoryOpen = (field) => {
    if(isSkipped[field] === true) {
      setDataLoaded((prevState) => ({
        ...prevState,
        [field]: false,
      }));
    }
  };

  const handleChange = (field, value, isOpen) => {
    const updatedFilterValues = getFilterValues({ field, value });
    const isDelayed = isOpen && DEFERRED_FILTERS.includes(field) && multiselectProcessDelayMS;

    onChange({
      ...JSON.parse(JSON.stringify(updatedFilterValues || {})),
      ...(field === MANUFACTURERS ? {
        [field]: value.reduce((result, id) => {
          const nameToIdsLocalMap = {
            ...nameToIdsMap,
            ...(hasEmptyManufacturers ? { '<Empty value>': ['empty'] } : {}),
          };

          const multipleIdManufacturer = nameToIdsLocalMap?.[manufacturerLocalNames?.[id.toString()]] || [];
          return [
            ...result?.filter((manufacturerId) => manufacturerId !== id),
            ...multipleIdManufacturer?.filter((manufacturerId) => manufacturerId === id),
          ];
        }, []),
      } : {}),
    }, isDelayed);
  };

  const sortingValue = getSortingValue({
    [SORT_DIRECTION]: filters[SORT_DIRECTION],
  });

  const categoriesQuery = useMemo(() => getFilterQueryData({ type: CATEGORIES, value: filters[CATEGORIES], valueMap: categoryNames }), [categoryValue]);
  const manufacturersQuery = useMemo(() => getFilterQueryData({ type: MANUFACTURERS, value: filters[MANUFACTURERS], valueMap: manufacturerLocalNames }), [manufacturersValue]);
  const storagesQuery = useMemo(() => {
    if (storageValue) {
      return getFilterQueryData({ type: STORAGE, value: filters[STORAGE], valueMap: storageLocationLocalNames });
    }

    return [];
  }, [storageValue]);

  const query = [...baseQuery, ...categoriesQuery?.data, ...manufacturersQuery.data, ...(Array.isArray(storagesQuery.data) ? storagesQuery.data : [])];

  const handleChangeRadio = () => {
    return (field, value) => {
      const updatedFilterValues = getFilterValues({ field, value });

      onChange(updatedFilterValues);
    };
  };

  const handleDelete = ({ type, value, text }) => {
    onDelete({
      type,
      ...(type === MANUFACTURERS && manufacturersQuery.namesMap[text]?.length > 1 ? {
        value: manufacturersQuery
          .namesMap[text],
      } : { value }),
    });
  };

  return (
    <>
      {
        isShown ? (
          <>
            {
              isQueryDisplayed && query.length && !isQueryFilterSibling ? (
                <SearchQuery
                  data={query}
                  onDelete={handleDelete}
                  onReset={onReset}
                  isEditable={!isLoading}
                />
              ) : null
            }
            <Wrapper style={{ display: 'flex', flexDirection: isQueryFilterSibling ? 'column' : 'row', width: '100%' }}>
              {
                isQueryDisplayed && query.length && isQueryFilterSibling ? (
                  <SearchQuery
                    data={query}
                    onDelete={handleDelete}
                    onReset={onReset}
                    isEditable={!isLoading}
                  />
                ) : null
              }
              <Container isLongerModal={isLongerModal}>
                {
                  filters[TYPE] === TYPES.SET || filters[TYPE] === TYPES.PROJECT || isSetOptionsContent ? (
                    <Dropdown
                      label="Sort by"
                      options={SET_SORTING_OPTIONS}
                      value={sortingValue}
                      onChange={(value) => handleChange(SORT_BY, value)}
                    />
                  ) : (
                    <>
                      {
                        hasAdditionalSortOptions ? (
                          <Dropdown
                            label="Sort by"
                            options={ITEM_SORTING_OPTIONS}
                            value={sortingValue}
                            onChange={(value) => handleChange(SORT_BY, value)}
                          />
                        ) : (
                          <Dropdown
                            label="Sort by"
                            options={DEFAULT_SORTING_OPTIONS}
                            value={filters[SORT_BY] === 'itemName' ? 'itemName' : sortingValue}
                            onChange={(value) => handleChange(SORT_BY, value)}
                          />
                        )
                      }
                    </>
                  )
                }
                <MultiSelect
                  label="Category"
                  key={CATEGORIES}
                  field={CATEGORIES}
                  searchPlaceholder="Search within categories"
                  options={categoriesOptions}
                  value={categoryValue}
                  labelMap={categoryNames}
                  onChange={handleChange}
                  onOpen={handleCategoryOpen}
                  onSearch={onCategoryQueryChange}
                  onClose={handleCategoriesClose}
                  isContentDisabledWhenOpen={isContentDisabledWhenOpen}
                />
                <MultiSelect
                  label="Manufacturer"
                  key={MANUFACTURERS}
                  field={MANUFACTURERS}
                  searchPlaceholder="Search within manufacturers"
                  options={hasEmptyManufacturers ? [{ text: '<Empty value>', value: 'empty' }, ...manufacturersOptions] : manufacturersOptions}
                  value={manufacturersValue}
                  onChange={handleChange}
                  onOpen={handleOpen}
                  onSearch={handleManufacturerSearch}
                  onClose={handleClose}
                  isContentDisabledWhenOpen={isContentDisabledWhenOpen}
                />
                {
                  (filters[TYPE] !== undefined && filters[TYPE] !== 'project' && showType) ? (
                    <RadioGroup
                      options={typeOptions}
                      value={filters[TYPE]}
                      field={TYPE}
                      label="Show"
                      onChange={handleChangeRadio('very important')}
                    />
                  ) : null
                }
                {
                  (filters[STORAGE] !== undefined && showStorage) ? (
                    <MultiSelect
                      label="Storage location"
                      key={STORAGE}
                      field={STORAGE}
                      searchPlaceholder="Search within storages"
                      options={hasEmptyStorages ? [{ text: '<Empty value>', value: 'empty' }, ...storagesOptions] : storagesOptions}
                      value={storageValue}
                      onSearch={onStorageQueryChange}
                      onChange={handleChange}
                      onOpen={handleOpen}
                      onClose={handleClose}
                      isContentDisabledWhenOpen={isContentDisabledWhenOpen}
                    />
                  ) : null
                }

                {
                  filters[QUANTITY] !== undefined ? (
                    <RadioGroup
                      options={qtyOptions}
                      value={filters[QUANTITY]}
                      field={QUANTITY}
                      label="Quantity"
                      onChange={handleChangeRadio('not very important')}
                    />
                  ) : null
                }
                {
                  (query.length || filters[SORT_DIRECTION] !== DIRECTION.DESC) ? (
                    <Reset onClick={onReset}>
                      <em>Reset filters</em>
                      <span>
                        <Icon className="icon-reload" />
                      </span>
                    </Reset>
                  ) : null
                }
              </Container>
              {children}
            </Wrapper>
          </>
        ) : null
      }
    </>
  );
};

export default Filters;
